<template>
  <component
    :is="tag"
    :aria-busy="isLoading"
    :aria-disabled="isDisabled"
    :class="{
      [$style.action]: true,
      [$style.action__disabled]: isDisabled,
      [$style.action__busy]: isLoading,
    }"
    :data-test-id="testId"
    :disabled="isDisabled"
    :form="form"
    :style="`
    --__width: ${width};
    --__min-width: ${minWidth};
    --__height: ${height};
    --__background-color: ${theme?.backgroundColor ?? Color.primary700};
    --__background-color-hover: ${theme?.backgroundColorHover ?? Color.primary600};
    --__border-color: ${theme?.borderColor ?? Color.primary700};
    --__text-color: ${theme?.textColor ?? Color.mono100};
    --__text-color-hover: ${theme?.textColorHover ?? Color.mono100}`"
    :type="type"
    v-bind="$attrs"
    @click="$emit('click')"
  >
    <template v-if="$slots.icon && !isLoading">
      <slot
        :class="$style.icon"
        name="icon"
      />
    </template>
    <span :class="$style.inner">
      <slot v-if="!isLoading" />
      <span
        v-else
        :class="$style.loading"
      >
        <LoadingBubble :color="theme?.loadingColor ?? '#ffffff'" />
      </span>
    </span>
  </component>
</template>

<script setup lang="ts">
import { ActionState } from './config/ActionState';
import type { ActionPropsType } from './config/ActionProps.type';
import { Color } from 'assets/colors/colors';
import { LoadingBubble } from '~/components-daisy/loading';

defineOptions({ inheritAttrs: false });

type Props = ActionPropsType & {
  tag: string;
  type?: string;
  width?: string;
  minWidth?: string;
  height?: string;
  form?: string | undefined;
  theme?: {
    backgroundColor?: string;
    backgroundColorHover?: string;
    borderColor?: string;
    textColor?: string;
    textColorHover?: string;
    loadingColor?: string;
  };
};
const props = withDefaults(defineProps<Props>(), {
  type: '',
  width: '100%',
  height: '3.125rem',
  form: undefined,
  theme: () => ({}),
  autofocus: false,
});

defineEmits(['click']);

const isDisabled = computed(() => {
  switch (props.state) {
    case ActionState.Idle:
      return false;
    case ActionState.Busy:
      return true;
    case ActionState.Disabled:
      return true;
    default:
      return undefined;
  }
});

const isLoading = computed(() => {
  switch (props.state) {
    case ActionState.Idle:
      return false;
    case ActionState.Busy:
      return true;
    case ActionState.Disabled:
      return false;
    default:
      return undefined;
  }
});
</script>

<style lang="postcss" module>
.action {
  justify-content: center;
  align-items: center;
  display: flex;
  gap: var(--space-100);
  outline: none;
  width: var(--__width);
  min-width: var(--__min-width, 8.125rem);
  max-width: 20.313rem;
  border-radius: var(--border-radius-600);
  box-shadow: var(--shadow-200);
  min-height: var(--__height);
  padding: var(--space-100);
  border: none;
  font: var(--font-400);
  font-size: 1rem;
  -moz-osx-font-smoothing: antialiased;
  -webkit-font-smoothing: antialiased;
  cursor: pointer;
  touch-action: manipulation;
  background: var(--__background-color);
  border: 1px solid var(--__border-color);
  color: var(--__text-color);

  &__disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &__busy {
    cursor: wait;
  }

  &:focus-visible {
    text-decoration: underline;
  }

  &:hover {
    transition: background-color 400ms;
    background: var(--__background-color-hover);
    color: var(--__text-color-hover);
  }
}

.icon {
  display: flex;
}

.inner {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  vertical-align: middle;
}

.loading {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
